/**
 * ContextAtlas (D2) — the "Where are we operating?" board (upd-1.md §23 / §18.1).
 * A browsable map of every context grouped by `context_type` (field, workflow,
 * principle, …). Each card shows the context name, a maturity badge, and the
 * "next step" hint for its stage (§7). Clicking a card opens the Context Detail.
 *
 * Data comes from the generic `contexts` CRUD list endpoint (`useList`), the
 * same client-side aggregation approach as the OKR dashboard. A free-text filter
 * narrows by name; an empty atlas degrades to a friendly placeholder (D3: the
 * board never errors on missing context — the Global Operating Context is always
 * seeded, so at least one card renders).
 */
import * as React from "react";
import { useList, useGo } from "@refinedev/core";

import type { Context, ContextType } from "@aos/shared";
import { CONTEXT_TYPES } from "@aos/shared";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { StatusBadge } from "@/components/app/StatusBadge";
import { humanize } from "@/lib/format";
import {
  EntityGraph,
  type EntityGraphNode,
} from "@/components/graph";

import { MaturityBadge, maturityNextStep } from "@/resources/contexts/MaturityBadge";

/** A single context card on the atlas board. */
function ContextCard({
  context,
  onOpen,
}: {
  context: Context;
  onOpen: (id: string) => void;
}) {
  return (
    <Card
      className="cursor-pointer transition-colors hover:bg-muted/40"
      onClick={() => onOpen(context.id)}
    >
      <CardHeader className="space-y-2 pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-sm leading-snug">{context.name}</CardTitle>
          <MaturityBadge stage={context.maturity_stage} />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline">{humanize(context.context_type)}</Badge>
          {context.status && context.status !== "active" ? (
            <StatusBadge status={context.status} />
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-1.5">
        {context.description ? (
          <p className="line-clamp-2 text-sm text-muted-foreground">
            {context.description}
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Next step:</span>{" "}
          {maturityNextStep(context.maturity_stage)}
        </p>
      </CardContent>
    </Card>
  );
}

/** One column/section per context_type, with its cards. */
function TypeSection({
  type,
  contexts,
  onOpen,
}: {
  type: ContextType;
  contexts: Context[];
  onOpen: (id: string) => void;
}) {
  if (contexts.length === 0) return null;
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold">{humanize(type)}</h2>
        <Badge variant="secondary">{contexts.length}</Badge>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {contexts.map((ctx) => (
          <ContextCard key={ctx.id} context={ctx} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}

/** Loading skeleton grid shown while the contexts list query is in flight. */
function AtlasSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-32 w-full" />
      ))}
    </div>
  );
}

export function ContextAtlas() {
  const go = useGo();
  const [filter, setFilter] = React.useState("");
  const [view, setView] = React.useState<"board" | "graph">("board");

  const { data, isLoading, isError, error } = useList<Context>({
    resource: "contexts",
    sorters: [{ field: "priority", order: "desc" }],
    pagination: { pageSize: 500 },
  });

  const contexts = React.useMemo(() => data?.data ?? [], [data]);

  /** Filter by name (case-insensitive substring). */
  const visible = React.useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return contexts;
    return contexts.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.description ?? "").toLowerCase().includes(q),
    );
  }, [contexts, filter]);

  /** Group the visible contexts by their context_type. */
  const byType = React.useMemo(() => {
    const map = new Map<ContextType, Context[]>();
    for (const c of visible) {
      const list = map.get(c.context_type) ?? [];
      list.push(c);
      map.set(c.context_type, list);
    }
    return map;
  }, [visible]);

  const openContext = React.useCallback(
    (id: string) => {
      go({ to: `/contexts/${id}` });
    },
    [go],
  );

  // ----- EntityGraph adapter (B2): contexts → clustered card field. -----
  // No edges: contexts host onto companies/projects, not each other, so the
  // value is a clustered 2D field by context_type (far richer than a flat grid).
  const graphNodes = React.useMemo<EntityGraphNode[]>(
    () =>
      visible.map((c) => ({
        id: c.id,
        title: c.name,
        type: c.context_type,
        subtitle: `${humanize(c.context_type)} · ${humanize(c.maturity_stage)}`,
        description: c.description ?? undefined,
        cluster: c.context_type,
        tag: c.maturity_stage ?? undefined,
        path: `/contexts/${c.id}`,
      })),
    [visible],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Context Atlas"
        subtitle="Where are we operating? Every context, grouped by type, with its maturity and next step."
        icon="contexts"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center rounded-md border border-border p-0.5">
              <Button
                variant={view === "board" ? "default" : "ghost"}
                size="sm"
                onClick={() => setView("board")}
              >
                <Icon name="contexts" size={16} className="mr-1.5" aria-hidden />
                Board
              </Button>
              <Button
                variant={view === "graph" ? "default" : "ghost"}
                size="sm"
                onClick={() => setView("graph")}
              >
                <Icon name="graph" size={16} className="mr-1.5" aria-hidden />
                Graph
              </Button>
            </div>
            <div className="relative w-full max-w-sm sm:w-64">
              <Icon
                name="search"
                size={16}
                className="pointer-events-none absolute left-2.5 top-2.5 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter contexts…"
                className="pl-8"
                aria-label="Filter contexts"
              />
            </div>
          </div>
        }
      />

      {isLoading ? (
        <AtlasSkeleton />
      ) : isError ? (
        <ErrorState
          error={error}
          message={
            error instanceof Error
              ? error.message
              : "Something went wrong while loading contexts."
          }
        />
      ) : visible.length === 0 ? (
        <EmptyState
          icon="contexts"
          title={filter ? "No contexts match your filter." : "No contexts yet."}
          hint={
            filter
              ? "Try a different search term."
              : "Define your first operating context to start mapping where you work."
          }
          action={
            filter ? undefined : (
              <Button onClick={() => go({ to: "/contexts/create" })}>
                <Icon name="create" size={16} className="mr-2" aria-hidden />
                New Context
              </Button>
            )
          }
        />
      ) : view === "graph" ? (
        <EntityGraph
          nodes={graphNodes}
          edges={[]}
          direction="LR"
          height="72vh"
          onNodeClick={(node) => openContext(node.id)}
          emptyMessage="No contexts to graph yet."
        />
      ) : (
        <div className="space-y-8">
          {CONTEXT_TYPES.map((type) => (
            <TypeSection
              key={type}
              type={type}
              contexts={byType.get(type) ?? []}
              onOpen={openContext}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ContextAtlas;
